import { Box, Card, CardContent, Typography, Chip, IconButton, Tooltip, Alert } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import Swal from "sweetalert2";
import { Delete as DeleteIcon, VideoLibrary as VideoIcon, Code as HtmlIcon } from "@mui/icons-material";
import DocCardImage from "../DocCardImage/DocCardImage";
import { useTranslation } from 'react-i18next';

const FileDropZone = ({ setParentData, maxFiles = 1 }) => {
  const { t } = useTranslation();
  const [alert, setAlert] = useState("");
  const [formData, setFormData] = useState({ files: [] });
  const [validationWarnings, setValidationWarnings] = useState([]);

  useEffect(() => {
    if (alert !== "") {
      Swal.fire({ icon: "warning", text: alert, confirmButtonText: "Okay" });
    }
  }, [alert]);

  // FIXED: Added MP4/MOV and HTML support
  const ACCEPT = {
    "application/pdf": [".pdf"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "text/csv": [".csv"],
    "text/plain": [".txt"],
    "image/png": [".png"],
    "image/jpeg": [".jpeg", ".jpg"],
    "image/gif": [".gif"],
    // NEW: MP4/MOV video support
    "video/mp4": [".mp4"],
    "video/quicktime": [".mov", ".MOV"],
    //  NEW: HTML support
    "text/html": [".html", ".htm"],
  };

  //  NEW: File size limits matching backend
  const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB
  const MAX_HTML_SIZE = 10 * 1024 * 1024; // 10MB for HTML
  const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB for videos

  const isVideoUpload = (file) =>
    file?.type === "video/mp4" ||
    file?.type === "video/quicktime" ||
    /\.(mp4|mov)$/i.test(file?.name || "");

  //  NEW: Client-side validation
  const validateFile = (file) => {
    const warnings = [];

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      warnings.push(`${file.name}: File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds 500MB limit`);
      return { valid: false, warnings };
    }

    // Specific validation for HTML files
    if (file.type === "text/html" || file.name.match(/\.(html|htm)$/i)) {
      if (file.size > MAX_HTML_SIZE) {
        warnings.push(`${file.name}: HTML file size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds 10MB limit`);
        return { valid: false, warnings };
      }
    }

    // Specific validation for MP4/MOV videos
    if (isVideoUpload(file)) {
      if (file.size > MAX_VIDEO_SIZE) {
        warnings.push(`${file.name}: Video size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds 500MB limit`);
        return { valid: false, warnings };
      }
      // Add info about duration check
      warnings.push(`${file.name}: Video duration will be validated on upload (max 2 hours)`);
      return { valid: true, warnings };
    }

    return { valid: true, warnings };
  };

  const onDropAccepted = (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;

    const allWarnings = [];
    const validFiles = [];

    // NEW: Validate each file
    for (const file of acceptedFiles) {
      const validation = validateFile(file);
      
      if (validation.valid) {
        validFiles.push(file);
        if (validation.warnings.length > 0) {
          allWarnings.push(...validation.warnings);
        }
      } else {
        allWarnings.push(...validation.warnings);
      }
    }

    if (validFiles.length === 0) {
      Swal.fire({ 
        icon: "error", 
        title: "No valid files",
        text: allWarnings.join("\n"),
        confirmButtonText: "Okay" 
      });
      return;
    }

    let next = [...(formData.files || []), ...validFiles];
    if (maxFiles && next.length > maxFiles) {
      next = next.slice(0, maxFiles);
      Swal.fire({ icon: "info", text: `${t('GENERAL.FILES_ALLOWED').replace('file(s)', maxFiles + ' file(s)')}`, confirmButtonText: "Okay" });
    }

    setFormData((prev) => ({ ...prev, files: next }));
    if (setParentData) setParentData((prev) => ({ ...prev, files: next }));
    
    // Show warnings if any
    if (allWarnings.length > 0) {
      setValidationWarnings(allWarnings);
    } else {
      setValidationWarnings([]);
    }
    
    setAlert("");
  };

  const onDropRejected = (fileRejections) => {
    const names = (fileRejections || []).map((r) => r.file?.name).filter(Boolean).join(", ");
    
    //  FIXED: Updated error message to include MP4 and HTML
    const msg = names
      ? `${t('GENERAL.UNSUPPORTED_FILE_TYPES')} ${names}. ${t('GENERAL.PLEASE_UPLOAD_IMAGE_PDF_WORD_EXCEL_OR_CSV_FILES')}`
      : `${t('GENERAL.UNSUPPORTED_FILE_TYPE_PLEASE_UPLOAD_IMAGE_PDF_WORD_EXCEL_OR_CSV_FILES')}`;
    Swal.fire({ icon: "warning", text: msg, confirmButtonText: "Okay" });
    setAlert(msg);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPT,
    multiple: maxFiles !== 1,
    onDropAccepted,
    onDropRejected,
    useFsAccessApi: false,
    // NEW: Add maxSize to dropzone (this is a soft limit, we validate in onDropAccepted)
    maxSize: MAX_FILE_SIZE,
  });

  const handleDeleteFile = (indexToDelete) => {
    const updatedFiles = formData.files.filter((_, index) => index !== indexToDelete);
    setFormData((prev) => ({ ...prev, files: updatedFiles }));
    if (setParentData) setParentData((prev) => ({ ...prev, files: updatedFiles }));
    
    // Clear warnings if no files left
    if (updatedFiles.length === 0) {
      setValidationWarnings([]);
    }
  };

  // NEW: Helper to get file icon based on type
  const getFileIcon = (file) => {
    if (isVideoUpload(file)) {
      return <VideoIcon color="primary" />;
    }
    if (file.type === "text/html" || file.name.match(/\.(html|htm)$/i)) {
      return <HtmlIcon color="secondary" />;
    }
    return <DocCardImage type={file.type} />;
  };

  // NEW: Helper to format file type label
  const getFileTypeLabel = (file) => {
    if (isVideoUpload(file)) {
      const ext = (file.name?.split(".").pop() || "").toUpperCase();
      return ext === "MOV" ? "MOV Video" : "MP4 Video";
    }
    if (file.type === "text/html" || file.name.match(/\.(html|htm)$/i)) {
      return "HTML";
    }
    const typeMap = {
      "application/pdf": "PDF",
      "application/msword": "Word",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word",
      "application/vnd.ms-excel": "Excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel",
      "text/csv": "CSV",
      "text/plain": "Text",
      "image/png": "PNG",
      "image/jpeg": "JPEG",
      "image/gif": "GIF",
    };
    return typeMap[file.type] || file.type || "Unknown";
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/*  NEW: Show validation warnings */}
      {validationWarnings.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setValidationWarnings([])}>
          {validationWarnings.map((warning, idx) => (
            <Typography key={idx} variant="caption" display="block">
              {warning}
            </Typography>
          ))}
        </Alert>
      )}

      <Box
        {...getRootProps()}
        sx={{
          border: "2px dotted #ccc",
          borderRadius: 1,
          padding: 2,
          textAlign: "center",
          cursor: "pointer",
          width: "100%",
          minHeight: "40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: isDragActive ? "#f0f0f0" : "transparent",
          "&:hover": {
            border: (theme) => `2px dotted ${theme.palette.primary.main}`,
            color: (theme) => theme.palette.primary.main,
          },
        }}
      >
        <input
          {...getInputProps({
            onClick: (e) => {
              e.target.value = null;
            },
          })}
        />
        <Typography sx={{ "&:hover": { color: (theme) => theme.palette.primary.main } }}>
          {formData.files && formData.files.length > 0
            ? t('GENERAL.CLICK_TO_ADD_MORE_FILES_OR_DRAG_AND_DROP_HERE')
            : t('GENERAL.DRAG_AND_DROP_FILES_HERE_OR_CLICK_TO_SELECT')}
        </Typography>
      </Box>

      {formData.files && formData.files.length > 0 && (
        <Box sx={{ width: "100%", mt: 2 }}>
          <Typography variant="body2" color="textSecondary" mb={1}>
            {t('GENERAL.ATTACHED_FILES')} ({formData.files.length})
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {formData.files.map((file, index) => (
              <Card key={index} variant="outlined" sx={{ width: "100%", "&:hover": { backgroundColor: "action.hover" } }}>
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1.5,
                    "&:last-child": { pb: 1.5 },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                    {/*  IMPROVED: Better icon handling */}
                    {getFileIcon(file)}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {file.size >= 1024 * 1024 
                          ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                          : `${(file.size / 1024).toFixed(1)} KB`
                        }
                      </Typography>
                    </Box>
                    <Tooltip title={file.type || "Unknown type"}>
                      <Chip 
                        label={getFileTypeLabel(file)} 
                        size="small" 
                        variant="outlined" 
                        color={isVideoUpload(file) ? "primary" : file.type === "text/html" ? "secondary" : "default"}
                        sx={{ ml: 1, mr: 1, maxWidth: 120, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} 
                      />
                    </Tooltip>
                  </Box>
                  <IconButton onClick={() => handleDeleteFile(index)} size="small" color="error" sx={{ ml: 1 }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FileDropZone;
